/**
 * The story body format — deliberately tiny, so a parent can write it on a
 * phone in a plain textarea:
 *
 *   a blank line      starts a new paragraph (single line breaks join up)
 *   **words**         bold
 *   "- " at the start a bullet (consecutive bullets form one list)
 *   "## " at the start a small heading
 *   {{name}}          an editor-placed block (see STORY_EMBEDS), alone on its line
 *
 * Parsing yields plain data that StoryBody renders as React elements — there
 * is no HTML anywhere, so a submission can never inject markup.
 */
import { EMBED_LINE } from "@shared/lib/storiesText";
import { STORY_EMBEDS, type StoryEmbed } from "./contract";

// The counting helpers are shared with the server, so both sides count alike.
export { countWords, readingMinutes, stripEmbeds } from "@shared/lib/storiesText";

export interface InlineSegment {
  text: string;
  bold: boolean;
}

export type StoryBlock =
  | { kind: "paragraph"; segments: InlineSegment[] }
  | { kind: "heading"; text: string }
  | { kind: "list"; items: InlineSegment[][] }
  | { kind: "embed"; name: StoryEmbed };

const BULLET = /^[-*•]\s+/;
const HEADING = /^#{2,3}\s+/;

function isEmbedName(name: string): name is StoryEmbed {
  return (STORY_EMBEDS as readonly string[]).includes(name);
}

/** Splits "**bold**" runs out of a line of text. Unclosed ** stays literal. */
export function parseInline(text: string): InlineSegment[] {
  const segments: InlineSegment[] = [];
  const pattern = /\*\*(.+?)\*\*/g;
  let last = 0;
  for (let match = pattern.exec(text); match; match = pattern.exec(text)) {
    if (match.index > last) segments.push({ text: text.slice(last, match.index), bold: false });
    segments.push({ text: match[1] ?? "", bold: true });
    last = match.index + match[0].length;
  }
  if (last < text.length) segments.push({ text: text.slice(last), bold: false });
  return segments.length ? segments : [{ text, bold: false }];
}

/**
 * @param allowEmbeds editors' stories may place embeds; a member's preview
 *   never does (the server strips those lines from submissions too).
 */
export function parseStoryBody(body: string, { allowEmbeds = false } = {}): StoryBlock[] {
  const blocks: StoryBlock[] = [];
  const chunks = body.replace(/\r\n?/g, "\n").split(/\n\s*\n/);

  for (const chunk of chunks) {
    const lines = chunk
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean);
    let paragraph: string[] = [];
    let list: InlineSegment[][] = [];

    const flushParagraph = () => {
      if (paragraph.length) blocks.push({ kind: "paragraph", segments: parseInline(paragraph.join(" ")) });
      paragraph = [];
    };
    const flushList = () => {
      if (list.length) blocks.push({ kind: "list", items: list });
      list = [];
    };

    for (const line of lines) {
      const embed = EMBED_LINE.exec(line);
      if (embed) {
        flushParagraph();
        flushList();
        const name = embed[1] ?? "";
        if (allowEmbeds && isEmbedName(name)) blocks.push({ kind: "embed", name });
        continue;
      }
      if (HEADING.test(line)) {
        flushParagraph();
        flushList();
        blocks.push({ kind: "heading", text: line.replace(HEADING, "").replace(/\*\*/g, "") });
        continue;
      }
      if (BULLET.test(line)) {
        flushParagraph();
        list.push(parseInline(line.replace(BULLET, "")));
        continue;
      }
      flushList();
      paragraph.push(line);
    }
    flushParagraph();
    flushList();
  }
  return blocks;
}
